import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from "antd";
import { useLoaderData, useFetcher } from "react-router-dom";
import { auth } from "~/lib";
import { LeaseRenewalPageLoaderType } from "./loaders";
import * as APITypes from "~/API";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";
import { RenewalApplicationDetails } from "./components";
import { INTENTS } from "~/utils/constants";
import { CustomTextField } from "~/components";
import { useAppTranslation } from "~/hooks/useAppTranslation";

export const LeaseRenewalPage: React.FC = () => {
  const currentUser = auth.currentUser;
  const loaderData = useLoaderData() as LeaseRenewalPageLoaderType;
  const { t } = useAppTranslation();
  dayjs.extend(customParseFormat);
  const dateFormat = "YYYY/MM/DD";
  const [leaseValue, setLeaseValue] = useState(0);
  const [paymentValue, setPaymentValue] = useState(0);
  const fetcher = useFetcher();
  const isSubmitting = fetcher.state !== "idle";

  const onChangeLease = (value: number) => {
    setLeaseValue(value);
  };

  const onChangePayment = (value: number) => {
    setPaymentValue(value);
  };

  const renew = (values: Record<string, unknown>) => {
    fetcher.submit(
      {
        body: JSON.stringify({
          leaseValue,
          paymentValue,
          userDataLease: loaderData.activeLeaseData.leaseInfo,
          rent: loaderData.activeLeaseData.offer?.rent,
          tenantInfo: loaderData.tenantInfo,
          additionalComments: values.additionalRequestsNotes ?? "",
          nonRenewalReason: values.nonRenewalReason ?? "",
        }),
        intent: INTENTS._renew_lease,
      },
      { method: "post" }
    );
  };

  return (
    <>
      <Typography.Title level={1}>{t("Renew_Lease")}</Typography.Title>

      <Typography.Title level={2}>{t("Lease_Details")}</Typography.Title>

      {(loaderData.activeLeaseData?.leaseInfo?.status ===
        APITypes.LeaseStatusType.Terminating ||
        loaderData.nextLeaseData !== null) && (
        <Alert
          message={t("You_have_already_submitted_a_response")}
          type="info"
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        name="basic"
        layout="vertical"
        // className="form-grid"
        disabled
      >
        <Row gutter={12} style={{ marginTop: "2rem" }}>
          <Col span={8}>
            <Form.Item label={t("Name")} rules={[{ required: true }]}>
              <Input
                defaultValue={
                  currentUser?.displayName ??
                  `${loaderData.tenantInfo.firstName} ${loaderData.tenantInfo.lastName}`
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t("Email")} rules={[{ required: true }]}>
              <Input defaultValue={currentUser?.email ?? ""} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t("Phone_Number")} rules={[{ required: true }]}>
              <Input defaultValue={currentUser?.phoneNumber ?? ""} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12} style={{ marginTop: "2rem" }}>
          <Col span={8}>
            <Form.Item label={t("Rent")}>
              <InputNumber
                defaultValue={loaderData.activeLeaseData.offer?.rent ?? 0}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="date-picker" label={t("Start_Date")}>
              <DatePicker
                defaultValue={dayjs(
                  loaderData.activeLeaseData?.leaseInfo?.startDate,
                  dateFormat
                )}
                format={dateFormat}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="date-picker" label={t("End_Date")}>
              <DatePicker
                defaultValue={dayjs(
                  loaderData.activeLeaseData?.leaseInfo?.endDate,
                  dateFormat
                )}
                format={dateFormat}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12} style={{ marginTop: "2rem" }}>
          <Col span={8}>
            <Form.Item
              label={t(
                loaderData.activeLeaseData?.leaseInfo?.entityType ||
                  APITypes.EntityType.Unit
              )}
              rules={[{ required: true }]}
            >
              <Input
                defaultValue={loaderData.activeLeaseData?.entityDetails?.name}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Divider style={{ marginTop: "2rem" }} />

      <Typography.Title level={2}>
        {t("Lease_Renewal_Options")}
      </Typography.Title>

      {loaderData.activeLeaseData?.leaseInfo.status ===
      APITypes.LeaseStatusType.Terminating ? (
        <CustomTextField value={loaderData.activeLeaseData?.comment?.text} />
      ) : loaderData.nextLeaseData === null ? (
        <Form
          name="basic"
          onFinish={renew}
          layout="vertical"
          disabled={isSubmitting}
        >
          <Row gutter={12} style={{ marginTop: "2rem" }}>
            <Col span={8}>
              <Form.Item
                name="Lease"
                label={t("Lease_Renewal_Options")}
                rules={[{ required: true }]}
              >
                <Select
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                  value={leaseValue}
                  onChange={onChangeLease}
                  options={[
                    {
                      value: 0,
                      label: t("1_year"),
                    },
                    {
                      value: 5,
                      label: t("2_years"),
                    },
                    {
                      value: 6,
                      label: t("3_years"),
                    },
                    {
                      value: 1,
                      label: t("6_months"),
                    },
                    {
                      value: 2,
                      label: t("3_months"),
                    },
                    {
                      value: 3,
                      label: t("1_month"),
                    },
                    {
                      value: 4,
                      label: "Not renewing (Provide reason)",
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="Payment"
                label={t("Preferred_Rent_Payment_Method")}
                rules={[{ required: true }]}
              >
                <Select
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                  value={paymentValue}
                  onChange={onChangePayment}
                  options={[
                    {
                      value: 1,
                      label: t("Bank_Transfer"),
                    },
                    {
                      value: 2,
                      label: t("Credit_Debit_Card"),
                    },
                    {
                      value: 3,
                      label: t("Check"),
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="additionalRequestsNotes"
                label={t("Additional_Requests_or_Notes")}
              >
                <Input.TextArea />
              </Form.Item>
            </Col>
            {leaseValue === 4 && (
              <Col span={8}>
                <Form.Item
                  name="nonRenewalReason"
                  label={t("Reason")}
                  rules={[
                    { required: true, message: t("Required") },
                  ]}
                >
                  <Input placeholder={t("Reason")} />
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item
            name="agreement1"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error("Should accept agreement")),
              },
            ]}
          >
            <Checkbox>
              I acknowledge that my lease renewal request is subject to approval
              by management.
            </Checkbox>
          </Form.Item>

          {/* <Form.Item
                    name="agreement2"
                    valuePropName="checked"
                    rules={[
                      {
                        validator: (_, value) =>
                          value
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error("Should accept agreement")
                              ),
                      },
                    ]}
                  >
                    <Checkbox>
                      I agree to the terms of the lease renewal policy.
                    </Checkbox>
                  </Form.Item> */}

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              {t("Submit")}
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <RenewalApplicationDetails nextLeaseData={loaderData.nextLeaseData} />
      )}
    </>
  );
};
